import Konva from '../../../konva';
import { TOOLS_IDS } from './constants';
import {
  resolveCanvasFontStyle,
  resolveFontFamilyForCanvas,
} from './normalizeAnnotationFontStylesForLoad';

/** Small buffer for cross-browser font metric drift vs when templates were saved. */
export const MEASUREMENT_BUFFER = 4;

export const hasExplicitLineBreaks = (text) => String(text || '').includes('\n');

const createMeasuredTextNode = (annotation, width) => {
  const textNode = new Konva.Text({
    text: annotation.text ?? '',
    fontSize: annotation.fontSize ?? 14,
    fontFamily: resolveFontFamilyForCanvas(annotation.fontFamily ?? 'Arial'),
    fontStyle: resolveCanvasFontStyle(
      annotation.fontFamily,
      annotation.fontStyle,
    ),
    letterSpacing: annotation.letterSpacing ?? 0,
    lineHeight: annotation.lineHeight ?? 1,
    padding: annotation.padding ?? 0,
    align: annotation.align ?? 'left',
    wrap: annotation.wrap ?? 'word',
    ...(width != null && width > 0 ? { width } : {}),
  });
  textNode._setTextData();
  return textNode;
};

export const getMeasuredBox = (annotation, width) => {
  const textNode = createMeasuredTextNode(annotation, width);
  const padding = textNode.padding() * 2;
  const lineCount = Math.max(textNode.textArr?.length || 1, 1);
  return {
    width: textNode.getTextWidth() + padding,
    height: textNode.fontSize() * lineCount * textNode.lineHeight() + padding,
    lineCount,
    textNode,
  };
};

/** True when rendered lines need more vertical space than the saved box height. */
export const isTextClippedAtHeight = (annotation, width) => {
  const savedHeight = annotation.height || 0;
  if (savedHeight <= 0) {
    return false;
  }
  const { height: needed } = getMeasuredBox(
    annotation,
    width && width > 0 ? width : undefined,
  );
  return needed > savedHeight + 0.5;
};

/** True when Konva word-wrap at `width` drops words from the saved annotation text. */
export const isTextClippedAtWidth = (annotation, width) => {
  if (!width || width <= 0) {
    return false;
  }
  const { textNode } = getMeasuredBox(annotation, width);
  const rendered = (textNode.textArr || []).map(({ text }) => text).join(' ');
  const words = String(annotation.text || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0) {
    return false;
  }
  return words.some((word) => !rendered.includes(word));
};

/** Deliberate word-wrap: no \\n, multiple rows at saved width, box height fits those rows. */
export const isIntentionalMultiLineWrap = (annotation, width) => {
  if (!width || width <= 0 || hasExplicitLineBreaks(annotation.text)) {
    return false;
  }
  const savedHeight = annotation.height || 0;
  if (savedHeight <= 0) {
    return false;
  }
  const atWidth = getMeasuredBox(annotation, width);
  if (atWidth.lineCount <= 1) {
    return false;
  }
  // Saved box is tall enough for the wrapped rows — user narrowed width on purpose.
  return savedHeight + 0.5 >= atWidth.height;
};

/** Single-line copy (no \\n) wrapped onto multiple rows at the saved width. */
export const isUnwantedWrapAtWidth = (annotation, width) => {
  if (!width || width <= 0 || hasExplicitLineBreaks(annotation.text)) {
    return false;
  }
  if (isIntentionalMultiLineWrap(annotation, width)) {
    return false;
  }
  const { lineCount } = getMeasuredBox(annotation, width);
  return lineCount > 1;
};

/** Keep center/right copy anchored when the text box width changes. */
export const adjustTextBoxXForWidthChange = (annotation, nextWidth) => {
  const savedWidth = Number(annotation.width) || 0;
  const align = annotation.align || 'left';
  let nextX = Number(annotation.x) || 0;
  if (savedWidth > 0 && Math.abs(nextWidth - savedWidth) >= 0.5) {
    if (align === 'center') {
      nextX += (savedWidth - nextWidth) / 2;
    } else if (align === 'right') {
      nextX += savedWidth - nextWidth;
    }
  }
  return nextX;
};

export const expandTextAnnotationToFit = (annotation) => {
  if (annotation?.name !== TOOLS_IDS.TEXT || !annotation.text) {
    return annotation;
  }

  const savedWidth = annotation.width || 0;
  const savedHeight = annotation.height || 0;

  if (savedWidth <= 0 && savedHeight <= 0) {
    return annotation;
  }

  if (savedWidth > 0 && isIntentionalMultiLineWrap(annotation, savedWidth)) {
    const atWidth = getMeasuredBox(annotation, savedWidth);
    const height = Math.max(savedHeight, atWidth.height) + MEASUREMENT_BUFFER;

    if (
      Math.abs(savedWidth - (annotation.width || 0)) < 0.5 &&
      Math.abs(height - savedHeight) < 0.5
    ) {
      return annotation;
    }

    return {
      ...annotation,
      width: savedWidth,
      height,
    };
  }

  const singleLineIntent = !hasExplicitLineBreaks(annotation.text);

  // Single-line copy: hug one row at natural width — never keep excess height from drag-to-draw.
  if (singleLineIntent) {
    const natural = getMeasuredBox(annotation);
    const width = Math.max(savedWidth, natural.width) + MEASUREMENT_BUFFER;
    const fitted = getMeasuredBox(annotation, width);
    const height = fitted.height + MEASUREMENT_BUFFER;

    if (
      Math.abs(width - savedWidth) < 0.5 &&
      Math.abs(height - savedHeight) < 0.5
    ) {
      return annotation;
    }

    return {
      ...annotation,
      x: adjustTextBoxXForWidthChange(annotation, width),
      width,
      height,
    };
  }

  let width = savedWidth;

  const shouldWiden =
    savedWidth > 0 &&
    (isTextClippedAtWidth(annotation, savedWidth) ||
      isTextClippedAtHeight(annotation, savedWidth) ||
      isUnwantedWrapAtWidth(annotation, savedWidth));

  if (shouldWiden) {
    const natural = getMeasuredBox(annotation);
    width = Math.max(savedWidth, natural.width) + MEASUREMENT_BUFFER;
  }

  const atTargetWidth = getMeasuredBox(annotation, width > 0 ? width : undefined);
  width = width > 0 ? width : atTargetWidth.width + MEASUREMENT_BUFFER;
  const height = Math.max(savedHeight, atTargetWidth.height) + MEASUREMENT_BUFFER;

  if (
    Math.abs(width - savedWidth) < 0.5 &&
    Math.abs(height - savedHeight) < 0.5
  ) {
    return annotation;
  }

  return {
    ...annotation,
    x: adjustTextBoxXForWidthChange(annotation, width),
    width,
    height,
  };
};

/**
 * Templates often store text boxes sized before webfonts finished loading (or on a
 * different OS). Konva clips/wraps inside the saved width — widen boxes when words
 * are missing or forced onto extra rows so "40% OFF" stays on one line.
 */
const expandTextAnnotationsToFitContent = (annotations) => {
  if (!annotations || typeof annotations !== 'object') {
    return annotations;
  }

  return Object.fromEntries(
    Object.entries(annotations).map(([id, annotation]) => [
      id,
      expandTextAnnotationToFit(annotation),
    ]),
  );
};

export default expandTextAnnotationsToFitContent;

/**
 * Second pass after Text nodes mount and webfonts load — patches annotation sizes
 * in editor state when Konva still clips or wraps single-line text incorrectly.
 */
export const collectClippedTextAnnotationPatches = (layer, annotations = {}) => {
  if (!layer?.find || !annotations) {
    return null;
  }

  const patches = {};

  layer.find('Text').forEach((node) => {
    const id = node.id();
    const annotation = annotations[id];
    if (!annotation || annotation.name !== TOOLS_IDS.TEXT) {
      return;
    }

    node._setTextData?.();

    const text = String(node.text() || '');
    const singleLineIntent = !text.includes('\n');
    const lineCount = Math.max(node.textArr?.length || 1, 1);
    const rendered = (node.textArr || []).map(({ text: line }) => line).join(' ');
    const words = text.trim().split(/\s+/).filter(Boolean);
    const savedWidth = annotation.width || node.width() || 0;
    const measureAnnotation = {
      ...annotation,
      text: node.text(),
      fontSize: node.fontSize(),
      fontFamily: node.fontFamily(),
      fontStyle: node.fontStyle(),
      letterSpacing: node.letterSpacing(),
      lineHeight: node.lineHeight(),
      padding: node.padding(),
      align: node.align(),
      width: savedWidth,
      height: annotation.height || node.height(),
    };
    const intentionalWrap = isIntentionalMultiLineWrap(
      measureAnnotation,
      savedWidth,
    );
    const clipped = words.some((word) => !rendered.includes(word));
    const unwantedWrap = singleLineIntent && lineCount > 1 && !intentionalWrap;

    if (!clipped && !unwantedWrap) {
      return;
    }

    if (intentionalWrap && !clipped) {
      return;
    }

    const padding = node.padding() * 2;
    let nextWidth = annotation.width || node.width();
    let nextHeight = annotation.height || node.height();

    if (intentionalWrap) {
      const atWidth = getMeasuredBox(measureAnnotation, savedWidth);
      nextWidth = savedWidth;
      nextHeight = Math.max(nextHeight, atWidth.height + MEASUREMENT_BUFFER);
    } else {
      if (!clipped && !unwantedWrap) {
        return;
      }

      if (clipped || unwantedWrap) {
        const natural = getMeasuredBox(measureAnnotation);
        nextWidth = Math.max(nextWidth, natural.width + MEASUREMENT_BUFFER);
      }

      if (singleLineIntent) {
        nextHeight =
          node.fontSize() * node.lineHeight() + padding + MEASUREMENT_BUFFER;
      } else {
        nextHeight = Math.max(
          nextHeight,
          node.fontSize() * lineCount * node.lineHeight() + padding + MEASUREMENT_BUFFER,
        );
      }
    }

    if (
      Math.abs(nextWidth - (annotation.width || 0)) >= 0.5 ||
      Math.abs(nextHeight - (annotation.height || 0)) >= 0.5
    ) {
      patches[id] = { ...annotation, width: nextWidth, height: nextHeight };
    }
  });

  if (Object.keys(patches).length === 0) {
    return null;
  }

  return {
    ...annotations,
    ...patches,
  };
};
